/**
 * Step4Financing — Phase 27 (was "Step 5" pre-refactor).
 *
 * Cash purchase toggle (top, prominent) + conditional loan inputs.
 * Phase 27.0.1 — added BRRRR refinance panel (dealType === "brrrr") that
 * captures ARV-driven refi assumptions (LTV %, refi rate, refi term, refi
 * closing costs). Pre-refactor wizard sent these fields as 0 defaults in
 * the submit payload; this hotfix exposes the actual UI controls so BRRRR
 * investors can model their cash-out refi properly.
 *
 * Verbatim port from DealWizard.jsx renderStep step === 5 branch.
 * Preserve as-is: toggleCash hard-sets down_payment_percent=100 and
 * interest_rate=0 when cash is ON — that's a deliberate UX nudge so the
 * engine receives sane defaults even if the user toggles back to loan mode.
 * Also persists the user's preferred default via localStorage.
 */
import { NumberField } from "./_shared";

export default function Step4Financing({ wizard }) {
  const { form, setAnalysis, dealType } = wizard;
  const isCash = !!form.analysis.is_cash_purchase;
  const isBRRRR = dealType === "brrrr";
  const toggleCash = (next) => {
    setAnalysis("is_cash_purchase", next);
    try { window.localStorage?.setItem("runidea.wizard.cash_purchase_default", next ? "1" : "0"); } catch (e) {
      // Phase 36 Batch 1 — log instead of silent swallow.
      console.warn("[Step4Financing] persist cash_purchase_default failed:", e);
    }
    if (next) {
      setAnalysis("down_payment_percent", 100);
      setAnalysis("interest_rate", 0);
    }
  };
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Financing</h2>
        <p className="mt-1 text-sm text-slate-500">Loan structure & one-time closing costs.</p>
      </div>

      {/* Phase 17 — prominent cash-purchase toggle at the top */}
      <div
        data-testid="cash-purchase-card"
        data-cash={isCash ? "true" : "false"}
        className={`rounded-2xl border-2 px-5 py-4 shadow-sm transition-colors ${
          isCash ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`inline-flex h-10 w-10 items-center justify-center rounded-full text-xl ${isCash ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>
              💵
            </div>
            <div>
              <p className="text-base font-bold text-slate-900">Cash purchase — no loan</p>
              <p className="text-xs text-slate-500">
                When ON, we skip mortgage math and treat the whole purchase price as your cash investment.
              </p>
            </div>
          </div>
          <input
            type="checkbox"
            checked={isCash}
            onChange={(e) => toggleCash(e.target.checked)}
            data-testid="wizard-is-cash-purchase"
            className="h-6 w-6 cursor-pointer rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            aria-label="Cash purchase toggle"
          />
        </div>
      </div>

      {isCash ? (
        <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
          Loan inputs hidden. DSCR will be reported as <strong>N/A (cash)</strong> on the Financials tab.
        </p>
      ) : (
      <div className="grid gap-5 sm:grid-cols-2">
        <NumberField id="dp" label="Down payment" suffix="%"
          value={form.analysis.down_payment_percent}
          onChange={(v) => setAnalysis("down_payment_percent", v)}
          testid="analysis-down-payment" min={0} max={100}
          hint="Set 100% for all-cash deals." />
        <NumberField id="rate" label="Interest rate" suffix="%"
          value={form.analysis.interest_rate}
          onChange={(v) => setAnalysis("interest_rate", v)}
          testid="analysis-rate" min={0} max={50} />
        <NumberField id="term" label="Loan term" suffix="yrs"
          value={form.analysis.loan_term_years}
          onChange={(v) => setAnalysis("loan_term_years", v)}
          testid="analysis-term" min={1} max={60} />
        <NumberField id="closing" label="Closing costs" prefix="$"
          value={form.analysis.closing_costs}
          onChange={(v) => setAnalysis("closing_costs", v)}
          testid="analysis-closing" hint="Auto-suggested at ~2% of price." />
        <NumberField id="transfer" label="Transfer / stamp tax (one-time)" prefix="$"
          value={form.analysis.transfer_tax}
          onChange={(v) => setAnalysis("transfer_tax", v)}
          testid="analysis-transfer-tax" hint="Auto-filled from country defaults — added to total cash invested." />
      </div>
      )}

      {/* Phase 27.0.1 — BRRRR-only refinance panel. The wizard backup tracked
          refi_* fields in the submit payload but never exposed UI for them;
          this exposes the four levers that actually drive a BRRRR pro-forma:
          refi LTV (anchored on ARV from Step 2), refi rate, refi term, refi
          closing costs.
          Phase 47.1 — show the refi card on ALL BRRRR deals, including
          cash purchases. The canonical BRRRR is a cash buy followed by a
          refi against ARV — that's the whole thesis. Hiding the refi
          card in cash mode broke the most common BRRRR archetype. */}
      {isBRRRR ? (
        <div
          data-testid="brrrr-refi-card"
          className="rounded-2xl border-2 border-indigo-200 bg-indigo-50/60 px-5 py-4 shadow-sm"
        >
          <div className="mb-3 flex items-start gap-3">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-indigo-600 text-xl text-white">
              🔁
            </div>
            <div>
              <p className="text-base font-bold text-slate-900">Refinance assumptions</p>
              <p className="text-xs text-slate-500">
                After rehab + lease-up, BRRRR investors refinance against ARV
                (set on Step 3) and pull cash out. These four levers drive
                cash-on-cash, refi proceeds, and the new monthly payment.
              </p>
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <NumberField id="refi-ltv" label="Refi LTV" suffix="%"
              value={form.analysis.refi_ltv_pct ?? 75}
              onChange={(v) => setAnalysis("refi_ltv_pct", v)}
              testid="analysis-refi-ltv" min={0} max={100}
              hint="Most lenders cap BRRRR cash-outs at 70-75% of ARV." />
            <NumberField id="refi-rate" label="Refi interest rate" suffix="%"
              value={form.analysis.refi_interest_rate ?? 0}
              onChange={(v) => setAnalysis("refi_interest_rate", v)}
              testid="analysis-refi-rate" min={0} max={50}
              hint="Today's 30-yr investor rate — usually 0.5-1% over owner-occupied." />
            <NumberField id="refi-term" label="Refi loan term" suffix="yrs"
              value={form.analysis.refi_loan_term_years ?? 30}
              onChange={(v) => setAnalysis("refi_loan_term_years", v)}
              testid="analysis-refi-term" min={1} max={60} />
            <NumberField id="refi-closing" label="Refi closing costs" prefix="$"
              value={form.analysis.refi_closing_costs ?? 0}
              onChange={(v) => setAnalysis("refi_closing_costs", v)}
              testid="analysis-refi-closing"
              hint="Origination + appraisal + title — typically 2-3% of new loan." />
          </div>
        </div>
      ) : null}
    </div>
  );
}
